import { AxiosResponse } from "axios";
import { Dispatch, SetStateAction, useState } from "react";
import { useIntl } from "react-intl";

import { exportQuoteRequest, getBillOfLading } from "api";
import errorToast from "helpers/errorToast";

interface useGetDefaultFilesProps {
  shipmentId: string;
  booking_date?: string;
}

export interface useGetDefaultFilesReturn {
  isLoadingShipmentDetails: boolean;
  isLoadingBol: boolean;
  handleClickShipmentDetails: () => void;
  handleClickBol: () => void;
  shipmentDetailsFileName: string;
  bolFileName: string;

  defaultFilesMessage: {
    data: {
      key: string;
      type: string;
      value?: string | null;
      onClick?: () => void;
    }[];
  };
}

export default function useGetDefaultFiles({ shipmentId, booking_date }: useGetDefaultFilesProps): useGetDefaultFilesReturn {
  const intl = useIntl();
  const [isLoadingBol, setIsLoadingBol] = useState(false);
  const [bolPdfUrl, setBolPdfUrl] = useState<string | null>(null);
  const [isLoadingShipmentDetails, setIsLoadingShipmentDetails] = useState(false);
  const [shipmentDetailsPdfUrl, setShipmentDetailsPdfUrl] = useState<string | null>(null);

  const shipmentDetailsFileName = intl.formatMessage({ id: "general.shipmentDetails", defaultMessage: "Shipment Details" });
  const bolFileName = intl.formatMessage({ id: "tracking.billOfLading", defaultMessage: "Shipment Summary" });
  const shipmentSummaryFileNameWithRef = `${shipmentId} - Shipment Summary.pdf`;
  const shipmentDetailsFileNameWithRef = `${shipmentId} - Shipment Details.pdf`;

  const handleClickPdf = (url: string, fileName: string) => {
    // create a link element with the url and download the file
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("target", "_blank");
    link.setAttribute("download", fileName);
    link.click();
    link.remove();
    return link;
  };

  const handleClickShipmentDetailsOrBol = ({
    url,
    setPdfUrlState,
    setIsLoadingState,
    apiFunction,
    fileNameWithRef,
  }: {
    url: string | null;
    setPdfUrlState: Dispatch<SetStateAction<string | null>>;
    setIsLoadingState: Dispatch<SetStateAction<boolean>>;
    apiFunction: Promise<AxiosResponse<any, any>>;
    fileNameWithRef: string;
  }) => {
    // If the PDF has already been retrieved, download the file
    if (url) {
      handleClickPdf(url, fileNameWithRef);
      return;
    }

    setIsLoadingState(true);
    apiFunction
      .then((res) => {
        // Create a blob from the response
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        // Save the URL to state
        setPdfUrlState(url);

        // Open the PDF with proper filename handling
        // Use anchor element with download attribute to ensure filename is preserved
        handleClickPdf(url, fileNameWithRef);
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsLoadingState(false));
  };

  const handleClickShipmentDetails = () => {

    handleClickShipmentDetailsOrBol({
      url: shipmentDetailsPdfUrl,
      setPdfUrlState: setShipmentDetailsPdfUrl,
      setIsLoadingState: setIsLoadingShipmentDetails,
      apiFunction: exportQuoteRequest(shipmentId, "print-shipment-details"),
      fileNameWithRef: shipmentDetailsFileNameWithRef,
    });
  };

  const handleClickBol = () => {
    handleClickShipmentDetailsOrBol({
      url: bolPdfUrl,
      setPdfUrlState: setBolPdfUrl,
      setIsLoadingState: setIsLoadingBol,
      apiFunction: getBillOfLading(shipmentId),
      fileNameWithRef: shipmentSummaryFileNameWithRef,
    });
  };

  // Add a default message at the bottom of the activity list
  const defaultFilesMessage = {
    company: {
      company_name: "Cargobase (System)",
      company_logo: "/icon-192x192.png",
    },
    timestamp: booking_date,
    data: [
      {
        key: "attachment_url",
        type: "str",
        onClick: handleClickShipmentDetails,
        isLoading: isLoadingShipmentDetails,
      },
      {
        key: "attachment_name",
        type: "str",
        value: shipmentDetailsFileName,
      },
      {
        key: "attachment_size",
        type: "str",
        value: undefined,
      },
      {
        key: "attachment_url",
        type: "str",
        onClick: handleClickBol,
        isLoading: isLoadingBol,
      },
      {
        key: "attachment_name",
        type: "str",
        value: bolFileName,
      },
      {
        key: "attachment_size",
        type: "str",
        value: undefined,
      },
    ],
  };

  return {
    isLoadingShipmentDetails,
    isLoadingBol,
    handleClickShipmentDetails,
    handleClickBol,
    shipmentDetailsFileName,
    bolFileName,
    defaultFilesMessage,
  };
}
